import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../api';
import { useAuth } from '../../stores/auth';

export function Login() {
  const navigate = useNavigate();
  const setAuth = useAuth((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const r = await auth.login({ email, password });
      // Pick first membership as default active org; platform admins may have none
      const firstOrgId = r.memberships[0]?.org_id ?? null;
      setAuth({
        token: r.token,
        userId: r.user.id,
        email: r.user.email,
        name: r.user.name,
        isPlatformAdmin: r.user.is_platform_admin,
        memberships: r.memberships,
        activeOrgId: firstOrgId,
      });
      if (firstOrgId) navigate('/devices');
      else navigate('/platform/orgs'); // platform admin with no memberships -> orgs page
    } catch (e: unknown) {
      const detail = (e as { detail?: { error?: string } }).detail?.error;
      const status = (e as { status?: number }).status;
      if (status === 403 && detail === 'account_disabled') setErr('Account is disabled.');
      else if (status === 403 && detail === 'no_org_memberships') setErr('No org memberships. Contact your admin.');
      else setErr('Invalid credentials');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-box" onSubmit={submit}>
        <h1>IOTech Signage</h1>
        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
          />
        </div>
        <div className="field">
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        {err && <div className="error">{err}</div>}
        <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Signing in...' : 'Sign in'}
        </button>
        <div className="switch" style={{ color: 'var(--fg-3)' }}>
          New tenants are provisioned by your sysadmin.
        </div>
      </form>
    </div>
  );
}

export function AcceptInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setAuth = useAuth((s) => s.setAuth);
  const token = params.get('token') ?? '';

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<{
    email: string;
    role: string;
    orgSlug: string;
    orgName: string;
    userExists: boolean;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr('Missing invite token.');
      setLoading(false);
      return;
    }
    auth
      .inviteInfo(token)
      .then((r) => setInfo(r))
      .catch((e: unknown) => {
        const detail = (e as { detail?: { error?: string } }).detail?.error;
        if (detail === 'expired') setErr('This invite has expired. Ask for a new one.');
        else if (detail === 'already_used') setErr('This invite has already been used.');
        else setErr('Invalid or unknown invite link.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!info!.userExists && password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const payload: { token: string; password?: string; name?: string } = { token };
      if (!info!.userExists) {
        payload.password = password;
        payload.name = name || info!.email.split('@')[0];
      }
      const r = await auth.acceptInvite(payload);
      // After invite acceptance, force a fresh /me to load full profile + memberships
      const me = await auth.me();
      setAuth({
        token: r.token,
        userId: me.user.id,
        email: me.user.email,
        name: me.user.name,
        isPlatformAdmin: me.user.is_platform_admin,
        memberships: me.memberships,
        activeOrgId: r.orgId,
      });
      navigate('/devices');
    } catch (e: unknown) {
      const detail = (e as { detail?: { error?: string } }).detail?.error;
      setErr(detail || 'Could not accept invite.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="auth-screen"><div className="auth-box">Loading invite...</div></div>;
  }
  if (err && !info) {
    return (
      <div className="auth-screen">
        <div className="auth-box">
          <h1>Invite</h1>
          <div className="error">{err}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <form className="auth-box" onSubmit={submit}>
        <h1>Accept invite</h1>
        <p style={{ color: 'var(--fg-2)', lineHeight: 1.6 }}>
          You've been invited to <strong>{info!.orgName}</strong> as <strong>{info!.role}</strong>.
        </p>
        <div className="field">
          <label>Email</label>
          <input value={info!.email} disabled />
        </div>
        {!info!.userExists && (
          <>
            <div className="field">
              <label>Your name</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                placeholder={info!.email.split('@')[0]} />
            </div>
            <div className="field">
              <label>Set password (min 10 chars)</label>
              <input type="password" value={password} minLength={10}
                onChange={(e) => setPassword(e.target.value)} required autoFocus />
            </div>
            <div className="field">
              <label>Confirm password</label>
              <input type="password" value={confirm} minLength={10}
                onChange={(e) => setConfirm(e.target.value)} required />
            </div>
          </>
        )}
        {info!.userExists && (
          <p style={{ color: 'var(--fg-2)' }}>
            You already have an IOTech Signage account. Click below to add this org to your memberships.
          </p>
        )}
        {err && <div className="error">{err}</div>}
        <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Joining...' : info!.userExists ? 'Join org' : 'Activate account'}
        </button>
      </form>
    </div>
  );
}
